// ============================================================
// VidyaTrack — Landing Page Data  (centralised)
// ============================================================
import React from "react";
import {
  FiClipboard, FiCheck, FiBell, FiBookOpen,
  FiUsers, FiBarChart2, FiCalendar, FiZap,
  FiShield, FiGlobe, FiStar,
} from "react-icons/fi";

export const FEATURES = [
  { icon:<FiClipboard size={22}/>, title:"Homework Management",
    desc:"Assign tasks with deadlines, collect text & link submissions, give grades and feedback in one flow." },
  { icon:<FiCheck     size={22}/>, title:"Attendance Tracking",
    desc:"Mark present/absent/late in seconds. Visual monthly calendar. Instant low-attendance alerts." },
  { icon:<FiBell      size={22}/>, title:"Notice Board",
    desc:"Post urgent or general announcements. Pin notices. Target students, parents or teachers." },
  { icon:<FiBookOpen  size={22}/>, title:"Study Materials",
    desc:"Share Google Drive / YouTube links organised by class and subject. Students open with one click." },
  { icon:<FiUsers     size={22}/>, title:"Group Collaboration",
    desc:"Create named student groups, assign collaborative tasks with deadlines and track team progress." },
  { icon:<FiBarChart2 size={22}/>, title:"Analytics & Reports",
    desc:"Visual charts for attendance %, homework completion and individual student performance over time." },
  { icon:<FiCalendar  size={22}/>, title:"Academic Calendar",
    desc:"Colour-coded school events, exams, holidays and parent meetings in a shared monthly view." },
  { icon:<FiZap       size={22}/>, title:"Real-time Updates",
    desc:"Firebase Realtime Database pushes homework alerts, notice updates and attendance summaries instantly." },
];

export const ROLES = [
  { emoji:"🏫", label:"Admin",
    grad:"linear-gradient(135deg,#054bb4,#2e5caf)", light:"#e8eef8", clr:"#054bb4",
    points:["Manage students & teachers","Post school-wide notices","Full reports & analytics","Academic calendar control","Classes & sections management"] },
  { emoji:"👩‍🏫", label:"Teacher",
    grad:"linear-gradient(135deg,#059669,#10b981)", light:"#ecfdf5", clr:"#059669",
    points:["Assign homework with deadlines","Mark attendance daily","Share study material links","Create & manage groups","Give grades & feedback"] },
  { emoji:"👨‍🎓", label:"Student",
    grad:"linear-gradient(135deg,#7c3aed,#a78bfa)", light:"#f5f3ff", clr:"#7c3aed",
    points:["View & submit homework","Check attendance record","Open study materials","Read school notices","Collaborate in groups"] },
  { emoji:"👨‍👩‍👧", label:"Parent",
    grad:"linear-gradient(135deg,#d97706,#f59e0b)", light:"#fffbeb", clr:"#d97706",
    points:["Monitor child's homework","Track attendance live","Read all school notices","View academic calendar","Stay connected with school"] },
];

export const PLANS = [
  {
    tier:"Silver", icon:"🥈", price:"Rs. 149", suffix:"/student/mo",
    tagline:"Perfect for small schools",
    color:"#64748b", grad:"linear-gradient(135deg,#64748b,#94a3b8)",
    bg:"#f8fafc", border:"#e2e8f0",
    highlight:"Up to 300 students · 20 teachers",
    features:["Up to 300 students","Up to 20 teachers","Homework management","Daily attendance tracking","School notice board","Academic calendar","Study materials sharing","Email support"],
    notIncluded:["Group work module","Analytics & reports","Priority support","Custom branding"],
  },
  {
    tier:"Gold", icon:"🥇", price:"Rs. 199", suffix:"/student/mo",
    tagline:"Most popular — growing schools",
    color:"#d97706", grad:"linear-gradient(135deg,#d97706,#f59e0b)",
    bg:"linear-gradient(150deg,#054bb4 0%,#1e40af 100%)", border:"transparent",
    badge:"Most Popular", highlight:"Up to 800 students · 40 teachers", featured:true,
    features:["Up to 800 students","Up to 40 teachers","Everything in Silver","Group work module","Analytics & reports","Attendance charts","Priority email & phone support","Onboarding call included"],
    notIncluded:["Multi-campus","Custom branding","Dedicated manager"],
  },
  {
    tier:"Platinum", icon:"💎", price:"Contact Us", suffix:"",
    tagline:"For large institutions",
    color:"#7c3aed", grad:"linear-gradient(135deg,#7c3aed,#a78bfa)",
    bg:"#faf5ff", border:"#e9d5ff",
    highlight:"Unlimited students & teachers",
    features:["Unlimited students","Unlimited teachers","Everything in Gold","Multi-campus management","Custom school branding","Advanced analytics dashboard","API access","Dedicated account manager","On-site training sessions","SLA guarantee (99.9% uptime)"],
    notIncluded:[],
  },
];

export const TESTIMONIALS = [
  { init:"RP", name:"Ram Prasad Sharma",  role:"Principal, Shree Saraswati School, Kathmandu", clr:"#054bb4",
    text:"VidyaTrack completely transformed how we run our school. Attendance that used to take 20 minutes now takes 2. Parents stay informed automatically." },
  { init:"SK", name:"Sunita KC",          role:"Maths Teacher, Vidya Mandir, Lalitpur",         clr:"#059669",
    text:"The homework module is brilliant. I assign tasks, students submit with notes and links, I grade and give feedback — all in one place. My class is more engaged than ever." },
  { init:"BT", name:"Bimala Tamang",      role:"Parent, Class 9 Student, Bhaktapur",            clr:"#d97706",
    text:"I can see my son's homework and attendance from my phone at any time. I never miss a school notice now. VidyaTrack gives me real peace of mind." },
];

export const HIGHLIGHTS = [
  { icon:"⚡", label:"Set up in 1 day" },
  { icon:"🔒", label:"Google Firebase secured" },
  { icon:"📱", label:"Works on any device" },
  { icon:"🇳🇵", label:"Built in Nepal, priced in NPR" },
];

export const TRUST = [
  { icon:<FiShield size={16}/>, label:"Firebase Secured" },
  { icon:<FiZap    size={16}/>, label:"Real-time Sync" },
  { icon:<FiGlobe  size={16}/>, label:"Made in Nepal" },
  { icon:<FiStar   size={16}/>, label:"Local Support" },
];

export const HOW_IT_WORKS = [
  { n:"01", t:"Contact us",         d:"Reach out via phone, email or the form below. We will understand your school's needs and walk you through the platform." },
  { n:"02", t:"We set everything up",d:"We onboard your school, create accounts for admins, teachers, students and parents — fully configured for your setup." },
  { n:"03", t:"Go live in a day",   d:"Your school is live. Teachers start assigning, students submit, parents stay informed — all in real time." },
];

export const CONTACT_INFO = [
  { icon:"📧", label:"Email",   val:"titaniumcode.np@gmail.com",  href:"mailto:titaniumcode.np@gmail.com" },
  { icon:"📞", label:"Phone",   val:"+977-9804044517",          href:"tel:+9779804044517" },
  { icon:"📍", label:"Address", val:"Kathmandu, Nepal",         href:null },
];
